/**
 * The grid's sort, in the URL (issue #86).
 *
 * Sorting was the one part of a list held only inside the grid, so it was lost
 * on every way back to a list - the back button included, which kept the
 * filters. In the URL it is part of the link and is remembered with the filters
 * (see rememberedFilters).
 *
 * Simpler than useColumnFilters, because a sort has no half-built state: every
 * click is a finished question. What it does share is the router applying a
 * write as a transition. A controlled grid draws the model it is GIVEN, so two
 * quick clicks on a header would both start from the first click's stale model
 * (asc, then asc again rather than desc) - so the grid is handed the model it
 * last reported, and a write still in flight is not mistaken for somebody
 * else's navigation when it lands.
 */

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { GridSortModel } from '@mui/x-data-grid'
import { SORT_KEY, readSortModel, writeSortModel } from './rowFilter'

/** The sort as the URL would carry it, or null for unsorted. */
function asked(model: GridSortModel): string | null {
  return writeSortModel(new URLSearchParams(), model).get(SORT_KEY)
}

function parsed(raw: string | null): GridSortModel {
  const params = new URLSearchParams()
  if (raw !== null) params.set(SORT_KEY, raw)
  return readSortModel(params)
}

interface HeldSort {
  /** The URL's sort, as last reconciled against. */
  raw: string | null
  /** What the grid is showing. */
  model: GridSortModel
  /** Sorts written that the URL has not been seen to land on. */
  pending: (string | null)[]
}

export function useColumnSort() {
  const [params, setParams] = useSearchParams()
  const raw = params.get(SORT_KEY)

  const [held, setHeld] = useState<HeldSort>(() => ({ raw, model: parsed(raw), pending: [] }))

  // Adjusted during render, as useColumnFilters does, so the grid is never
  // handed one render of a sort the URL has already replaced.
  let current = held
  if (held.raw !== raw) {
    const ours = held.pending.indexOf(raw)
    current =
      ours >= 0
        ? { raw, model: held.model, pending: held.pending.slice(ours + 1) }
        : { raw, model: parsed(raw), pending: [] }
    setHeld(current)
  }

  const setSortModel = (next: GridSortModel) => {
    const text = asked(next)
    const heading = current.pending.length > 0 ? current.pending.at(-1) : current.raw
    const changes = text !== heading
    setHeld((was) => ({
      ...was,
      model: next,
      pending: changes ? [...was.pending, text] : was.pending,
    }))
    // Replace, not push: re-sorting must not fill up the back button.
    if (changes) setParams((now) => writeSortModel(now, next), { replace: true })
  }

  return { sortModel: current.model, setSortModel }
}
